<!-- src/views/admin/AdminManageDocumentsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & INTEGRATED COMMAND FILTERS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden">
          <div class="absolute top-0 right-0 p-12 opacity-5"><ClipboardDocumentListIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Document Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Policy Governance • Procedural Integrity & Audit Trail</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <button v-if="hasDocumentClearance" @click="navigateToCreate" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 border-none cursor-pointer">
                  <PlusIcon class="h-4 w-4" /> <span>Create Document</span>
                </button>

                <!-- 🛡️ NAVIGATION STANDARDISED: Updated button text to 'Return' and matched padding -->
                <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline">
                    <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                  </a>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10">
              <div class="flex flex-wrap gap-4 items-center">
                <div class="flex flex-col gap-1.5 min-w-[240px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                  <input v-model="filters.search" type="text" placeholder="Title or Reference..." class="filter-input-dark" />
                </div>

                <div class="flex flex-col gap-1.5 min-w-[240px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Registry Selection</span>
                  <select v-model="activeMode" class="filter-select-dark">
                    <option value="all">Full Document Registry ({{ documents.length }})</option>
                    <option v-for="cat in availableCategories" :key="cat" :value="cat">
                      {{ cat }} ({{ getCategoryCount(cat) }})
                    </option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 min-w-[160px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Status Filter</span>
                  <select v-model="filters.status" class="filter-select-dark">
                    <option value="all">All Statuses</option>
                    <option value="signed">Signed & Published</option>
                    <option value="pending">Awaiting Signature</option>
                  </select>
                </div>
              </div>

              <!-- 🛡️ RE-STYLED EXPORT BUTTON: Styled exactly like the dark glass Training export button -->
              <button 
                @click="exportToExcel"
                :disabled="exporting || filteredDocuments.length === 0"
                class="px-6 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 h-[46px] disabled:opacity-50 shadow-lg cursor-pointer border-none shrink-0"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS CARD -->
        <div class="mb-12 animate-fadeIn text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2">Active Document Control Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left animate-fadeIn">
                 <div v-for="stat in docStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current document registries, compliance signatures, and draft backlogs.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Document Trends</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Complete Policy Registry
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Document Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 3. AUTHORISATION NOTICE -->
        <div class="mb-10 bg-[#0A1630] rounded-3xl p-6 border-l-8 border-[#20FCF1] shadow-lg animate-fadeIn flex items-center gap-6">
           <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
             <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" />
           </div>
           <p class="text-[11px] font-bold text-slate-300 uppercase tracking-widest leading-relaxed text-left">
             Executive clearance is required for document publication. Only personnel with Director-level authorisation are synchronised to sign and formalise procedural records for global distribution.
           </p>
        </div>

        <!-- 4. DATA DISPLAY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Hydrating Document Registry...</p>
        </div>

        <div v-else-if="paginatedDocuments.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 animate-fadeIn shadow-inner">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No procedural records identified for this selection.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn">
          <div 
            v-for="docItem in paginatedDocuments" 
            :key="docItem.id"
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group"
          >
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
              
              <!-- DOC IDENTITY -->
              <div class="flex items-center gap-6 w-full xl:w-[35%] shrink-0">
                <div class="relative h-16 w-16 shrink-0 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-inner group-hover:bg-[#0A1630] transition-colors duration-500">
                  <DocumentTextIcon class="h-8 w-8 text-slate-300 group-hover:text-[#FD5E00]" />
                </div>
                <div class="min-w-0 text-left">
                  <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-tight truncate mb-2">
                    {{ docItem.title }}
                  </h3>
                  <div class="flex items-center gap-3">
                    <!-- Dynamic Document Reference Badge in Monospace font -->
                    <span v-if="docItem.reference" class="text-[9px] font-mono font-black text-[#20FCF1] uppercase tracking-widest bg-[#EBFDF8] px-2 py-0.5 rounded-md leading-none">{{ docItem.reference }}</span>
                    <span v-else class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest bg-[#EBFDF8] px-2 py-0.5 rounded-md leading-none">NO REF</span>
                    <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">Edited: {{ formatDate(docItem.lastEdited) }}</span>
                  </div>
                </div>
              </div>

              <!-- STATUS BADGE -->
              <div class="flex-grow px-8 border-l border-slate-50 min-w-0 text-left">
                  <span v-if="docItem.directorSignature" class="inline-flex items-center px-4 py-1.5 rounded-full text-[9px] font-black bg-[#EBFDF8] text-emerald-700 border border-emerald-100 uppercase tracking-widest shadow-sm">
                    <CheckCircleIcon class="h-3 w-3 mr-2" /> Signed & Published
                  </span>
                  <span v-else class="inline-flex items-center px-4 py-1.5 rounded-full text-[9px] font-black bg-amber-50 text-amber-700 border border-amber-100 uppercase tracking-widest shadow-sm">
                    <ExclamationTriangleIcon class="h-3 w-3 mr-2" /> Awaiting Executive Signature
                  </span>
              </div>

              <!-- ACTIONS -->
              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-3">
                 <button 
                    v-if="!docItem.directorSignature && isDirector && hasDocumentClearance" 
                    @click="signDocument(docItem.id)" 
                    class="p-4 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition-all shadow-lg cursor-pointer border-none"
                    title="Authorise Publication"
                  >
                    <CheckBadgeIcon class="h-5 w-5"/>
                  </button>

                  <button @click="viewDocument(docItem.id)" class="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:bg-[#0A1630] hover:text-white transition-all shadow-sm cursor-pointer border-none">
                    <EyeIcon class="h-5 w-5"/>
                  </button>

                  <button v-if="hasDocumentClearance" @click="navigateToEdit(docItem.id)" class="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-sm cursor-pointer border-none">
                    <PencilSquareIcon class="h-5 w-5" />
                  </button>

                  <button v-if="hasDocumentClearance" @click="deleteDocument(docItem.id)" class="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:bg-rose-500 hover:text-white transition-all shadow-sm cursor-pointer border-none">
                    <TrashIcon class="h-5 w-5" />
                  </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
        <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
          <div class="flex items-center gap-4">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              &larr; Prev
            </button>
            <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
              Page {{ currentPage }} of {{ totalPages || 1 }}
            </span>
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              Next &rarr;
            </button>
          </div>
          
          <!-- Symmetrical Items Per Page Selector (Forced h-11 height) -->
          <div class="flex items-center gap-3">
            <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
            <select 
              v-model="itemsPerPage" 
              class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
              style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
            >
              <option :value="25">25 Rows</option>
              <option :value="50">50 Rows</option>
              <option :value="100">100 Rows</option>
            </select>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted, computed, watch, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, onSnapshot, orderBy, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '@/firebaseConfig';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import { 
  PlusIcon, ClipboardDocumentListIcon, CheckCircleIcon, ExclamationTriangleIcon,
  PencilSquareIcon, TrashIcon, EyeIcon, CheckBadgeIcon,
  MagnifyingGlassIcon, ArrowUturnLeftIcon, ShieldCheckIcon, DocumentTextIcon,
  TableCellsIcon, LockClosedIcon, SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const router = useRouter();
const functions = getFunctions(auth.app, 'europe-west2');

const loading = ref(true);
const exporting = ref(false);
const documents = ref([]);
const availableCategories = ref(['Policies', 'Procedures']);
let unsubscribe = null;

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

// PAGINATION STATE (Defaulting to 25 rows with forced h-11 logic)
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ STICKY SELECTIONS & FILTERS: Read from localStorage dynamically
const activeMode = ref(localStorage.getItem('documents_sticky_mode') || 'all');

const filters = reactive({
  search: localStorage.getItem('documents_sticky_search') || '',
  status: localStorage.getItem('documents_sticky_status') || 'all'
});

// 🛡️ STICKY WATCHERS: Write filter selections to localStorage dynamically
watch(activeMode, (val) => localStorage.setItem('documents_sticky_mode', val));
watch(() => filters.search, (val) => localStorage.setItem('documents_sticky_search', val));
watch(() => filters.status, (val) => localStorage.setItem('documents_sticky_status', val));

// Reset page back to 1 on mode/filter updates
watch([activeMode, () => filters.search, () => filters.status, itemsPerPage], () => {
  currentPage.value = 1;
});

const isDirector = computed(() => {
  const jobTitle = userStore.userProfile?.jobTitle || '';
  return jobTitle.toLowerCase().includes('director');
});

const docStats = computed(() => {
    return [
      { label: 'Registry Size', value: documents.value.length, color: '#6366F1', icon: ClipboardDocumentListIcon },
      { label: 'Published', value: documents.value.filter(d => d.directorSignature).length, color: '#20FCF1', icon: CheckCircleIcon },
      { label: 'Draft / Pending', value: documents.value.filter(d => !d.directorSignature).length, color: '#FBBF24', icon: ExclamationTriangleIcon },
      { label: 'Clearance Tier', value: isDirector.value ? 'Executive' : 'Restricted', color: isDirector.value ? '#20FCF1' : '#EF4444', icon: LockClosedIcon },
    ];
});

const filteredDocuments = computed(() => {
  return documents.value.filter(docItem => {
    // Audit both the title and the dynamic reference key during query checks
    const titleMatch = !filters.search || 
      (docItem.title || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (docItem.reference || '').toLowerCase().includes(filters.search.toLowerCase());
      
    const typeMatch = activeMode.value === 'all' || docItem.type === activeMode.value;
    
    let statusMatch = true;
    if (filters.status === 'signed') statusMatch = !!docItem.directorSignature;
    if (filters.status === 'pending') statusMatch = !docItem.directorSignature;

    return titleMatch && typeMatch && statusMatch;
  });
});

// PAGINATION MATH
const totalPages = computed(() => {
  return Math.ceil(filteredDocuments.value.length / itemsPerPage.value);
});

const paginatedDocuments = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredDocuments.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

function formatDate(timestamp) {
  if (!timestamp || !timestamp.toDate) return 'N/A';
  return timestamp.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// 🛡️ RECONCILED IDENTITY RESOLUTION: Secure computed client tracking prevents race condition crashes
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

// 🛡️ UNCONDITIONAL PLATFORM AUTHORITY OVERRIDE: Simplifies permissions so all Command Access Admins can manage everything
const hasDocumentClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewDocuments === true ||
         userStore.permissions?.canManageDocuments === true;
});

// Helper to get real-time counts for client-defined categories
const getCategoryCount = (category) => {
  return documents.value.filter(d => d.type === category).length;
};

// --- Category Logic ---
const fetchClientCategories = async (clientId) => {
  try {
    const docRef = doc(db, 'clients', clientId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.documentCategories && Array.isArray(data.documentCategories)) {
        availableCategories.value = data.documentCategories;
      }
    }
  } catch (error) {
    console.error("Error fetching client categories:", error);
  }
};

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = currentClientId.value;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const total = documents.value.length;
        const publishedCount = documents.value.filter(d => d.directorSignature).length;
        const draftCount = documents.value.filter(d => !d.directorSignature).length;

        // Dynamic categories logging for synthesis intelligence
        const categorySummaryList = availableCategories.value.map(cat => {
          const count = getCategoryCount(cat);
          return `${cat}: ${count}`;
        });
        const categoryTrendsString = categorySummaryList.join(' / ');

        const publishRate = total > 0 ? Math.round((publishedCount / total) * 100) : 100;

        const report = {
            summary: `Document control and policy governance audit tracks a portfolio of ${total} core operational documents, reflecting a publication authorisation status of ${publishRate}%. Identified ${publishedCount} fully formalised policies and standards distributed globally, with ${draftCount} active drafts awaiting executive director endorsement.`,
            trends: [
                { label: 'Published Integrity', value: `${publishedCount} Released policies`, isCritical: publishRate < 70 },
                { label: 'Governance Backlog', value: `${draftCount} Pending signature drafts`, isCritical: draftCount > 2 },
                { label: 'Protocol Density', value: categoryTrendsString, isCritical: false }
            ],
            actions: [
                { type: 'Executive Action', instruction: `Sign off ${draftCount} pending drafts`, reason: 'Operational files remain restricted and invisible to standard users until formalised with a director signature.' },
                { type: 'Compliance Review', instruction: `Audit standard operating indexes`, reason: 'Systematic folder triages verify continuous structural alignment with safety laws.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'document_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'document_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Document Intelligence Registry Neutral.");
  }
};

const setupFirestoreListener = async (clientId) => {
  if (unsubscribe) unsubscribe();
  await fetchLatestAiReport(clientId);

  const documentsRef = collection(db, 'clients', clientId, 'documents');
  const q = query(documentsRef, orderBy('lastEdited', 'desc'));

  unsubscribe = onSnapshot(q, (snapshot) => {
    documents.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    loading.value = false;
  }, (error) => {
    console.error("Registry Sync Failure:", error);
    loading.value = false;
  });
};

const exportToExcel = () => {
  exporting.value = true;
  try {
    const data = filteredDocuments.value.map(d => ({
      'Document Title': d.title,
      'Reference': d.reference || 'N/A',
      'Classification': d.type || 'Policy',
      'Last Modified': formatDate(d.lastEdited),
      'Status': d.directorSignature ? 'Published' : 'Draft',
      'Authorised By': d.directorSignatureName || 'N/A'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Documents");
    XLSX.writeFile(wb, `Document_Control_Registry.xlsx`);
  } catch (err) { alert(err.message); } finally { exporting.value = false; }
};

// 🛡️ RECONCILED SYNC: Watches computed client ID reactively to resolve race condition crashes and load client categories
watch(currentClientId, async (newId) => {
  if (newId) {
    await fetchClientCategories(newId);
    setupFirestoreListener(newId);
  }
}, { immediate: true });

onUnmounted(() => { if (unsubscribe) unsubscribe(); });

const navigateToCreate = () => router.push({ name: 'CreateDocument' });
const navigateToEdit = (id) => router.push({ name: 'EditDocument', params: { id } });
const viewDocument = (id) => router.push({ name: 'ViewDocument', params: { documentId: id } });

async function deleteDocument(docId) {
  if (!confirm('Authorise permanent removal of this record? This action is immutable.')) return;
  try {
    await httpsCallable(functions, 'managePolicy')({ action: 'delete', policyData: { id: docId } });
  } catch (error) { alert(`Delete Failure: ${error.message}`); }
}

async function signDocument(docId) {
  if (!confirm('Authorise executive publication? This record will be formalised and distributed globally.')) return;
  try {
    await httpsCallable(functions, 'managePolicy')({ action: 'sign', policyData: { id: docId } });
  } catch (error) { alert(`Authorisation Failure: ${error.message}`); }
}
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-2xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-2xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>